/*
| Self-service two-factor (Fortify) on the profile page. Drives the native Fortify
| endpoints over axios (JSON): enable → fetch QR/secret/recovery → confirm with a
| code → enabled; or disable. State after confirm/disable is re-rendered by a full
| reload (server is the source of truth). All listeners are element-level (inside
| #screen) so they GC with the swapped node — no per-screen AbortSignal needed.
*/
const axios = (): any => (window as any).axios;

export const initTwoFactor = (): void => {
    const root = document.querySelector<HTMLElement>('[data-2fa]');
    if (!root) {
        return;
    }

    const url = (name: string): string => root.getAttribute(`data-2fa-${name}`) ?? '';
    const errorEl = root.querySelector<HTMLElement>('[data-2fa-error]');
    const setupEl = root.querySelector<HTMLElement>('[data-2fa-setup]');
    const activateBtn = root.querySelector<HTMLButtonElement>('[data-2fa-activate]');
    const confirmForm = root.querySelector<HTMLFormElement>('[data-2fa-confirm-form]');
    const disableBtn = root.querySelector<HTMLButtonElement>('[data-2fa-disable-btn]');

    const showError = (): void => {
        if (errorEl) {
            errorEl.hidden = false;
        }
    };
    const clearError = (): void => {
        if (errorEl) {
            errorEl.hidden = true;
        }
    };

    const loadSetup = async (): Promise<void> => {
        const [qr, secret, recovery] = await Promise.all([
            axios().get(url('qr')),
            axios().get(url('secret')),
            axios().get(url('recovery')),
        ]);

        const qrEl = root.querySelector<HTMLElement>('[data-2fa-qr-img]');
        const secretEl = root.querySelector<HTMLElement>('[data-2fa-secret-text]');
        const recoveryEl = root.querySelector<HTMLElement>('[data-2fa-recovery-list]');

        if (qrEl) {
            qrEl.innerHTML = qr.data.svg;
        }
        if (secretEl) {
            secretEl.textContent = secret.data.secretKey;
        }
        if (recoveryEl) {
            recoveryEl.innerHTML = '';
            (recovery.data as string[]).forEach((code) => {
                const li = document.createElement('li');
                li.textContent = code;
                recoveryEl.appendChild(li);
            });
        }
    };

    activateBtn?.addEventListener('click', async () => {
        clearError();
        activateBtn.disabled = true;
        try {
            await axios().post(url('enable'));
            await loadSetup();
            activateBtn.hidden = true;
            setupEl?.removeAttribute('hidden');
        } catch {
            showError();
            activateBtn.disabled = false;
        }
    });

    confirmForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError();
        const code = new FormData(confirmForm).get('code');
        try {
            await axios().post(url('confirm'), { code });
            window.location.reload();
        } catch {
            showError();
        }
    });

    disableBtn?.addEventListener('click', async () => {
        clearError();
        disableBtn.disabled = true;
        try {
            await axios().delete(url('disable'));
            window.location.reload();
        } catch {
            showError();
            disableBtn.disabled = false;
        }
    });

    // A returning user mid-setup (secret created, not yet confirmed) sees the QR again.
    if (root.getAttribute('data-2fa-state') === 'pending') {
        void loadSetup();
    }
};
